"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList, parseNumberOr } from "@/lib/parse-number";

export type CurveMethod = "flat" | "sqrt" | "scale";

function parseGrades(text: string): number[] {
  return parseNumberList(text).filter((n) => n >= 0);
}

const GRADE_CAP = 100;

/**
 * Keeps a curved grade at or below 100 without ever lowering a score.
 *
 * A grade already at or above the cap (extra credit) is left as entered: the square-root
 * curve would otherwise turn 121 into 110, and a flat boost would turn 105 into 100, so
 * curving would take marks away. Below the cap the result stops at 100, so the order of
 * students never changes.
 */
function capAtHundred(grade: number, curved: number): number {
  if (grade >= GRADE_CAP) {
    return grade;
  }
  return Math.min(GRADE_CAP, curved);
}

export function curveGrade(
  grade: number,
  method: CurveMethod,
  boost: number,
  max: number
): number {
  if (method === "flat") {
    return capAtHundred(grade, grade + boost);
  }
  if (method === "sqrt") {
    return capAtHundred(grade, 10 * Math.sqrt(grade));
  }
  if (max <= 0) {
    return grade;
  }
  // The top score maps to exactly 100; the min only absorbs floating-point error.
  return Math.min(GRADE_CAP, grade * (GRADE_CAP / max));
}

function average(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const GRADE_CURVE_CALCULATOR_STRINGS = {
  curveMethodFlat: "Flat boost (+N points)",
  curveMethodSqrt: "Square-root curve (10 × √grade)",
  curveMethodScale: "Scale highest to 100",
  gradesInputLabel: "Grades — one per line or comma-separated",
  inputPlaceholder: "72, 85, 91\n64\n78",
  gradesDetected: "one:{count} grade detected|other:{count} grades detected",
  curveMethod: "Curve method",
  pointsToAdd: "Points to add",
  cappedNote:
    "Curved grades stop at 100. A score already at or above 100, such as extra credit, is left as entered.",
  averageBefore: "Average before",
  averageAfter: "Average after",
  averageChange: "Average change",
  highestBefore: "Highest before",
  beforeAfter: "Before / After",
  copied: "Copied!",
  copyCurvedList: "Copy Curved List",
  columnNumber: "#",
  columnOriginal: "Original",
  columnCurved: "Curved",
  columnChange: "Change",
} as const;

export default function GradeCurveCalculatorWidget() {
  const [input, setInput] = useState("");
  const [method, setMethod] = useState<CurveMethod>("flat");
  const [boost, setBoost] = useState("5");
  const [copied, setCopied] = useState(false);

  const locale = useToolLocale();
  const strings = useToolUi(GRADE_CURVE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const curveMethods: { value: CurveMethod; label: string }[] = [
    { value: "flat", label: strings.curveMethodFlat },
    { value: "sqrt", label: strings.curveMethodSqrt },
    { value: "scale", label: strings.curveMethodScale },
  ];

  const grades = parseGrades(input);
  const boostValue = parseNumberOr(boost, 0);
  const maxGrade = grades.length > 0 ? Math.max(...grades) : 0;
  const curved = grades.map((g) => curveGrade(g, method, boostValue, maxGrade));
  const avgBefore = average(grades);
  const avgAfter = average(curved);

  function handleCopy() {
    if (curved.length === 0) {
      return;
    }
    const text = curved.map((g) => g.toFixed(1)).join("\n");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="curve-input"
        >
          {strings.gradesInputLabel}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="curve-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.gradesDetected, locale, grades.length)}
        </p>
      </div>

      {/* Curve method */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.curveMethod}
        </p>
        <div className="flex flex-wrap gap-3">
          {curveMethods.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={method === m.value}
                className="accent-primary"
                name="curve-method"
                onChange={() => setMethod(m.value)}
                type="radio"
                value={m.value}
              />
              {m.label}
            </label>
          ))}
        </div>
        {method === "flat" && (
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <label
                className="block font-medium text-foreground text-xs"
                htmlFor="boost-points"
              >
                {strings.pointsToAdd}
              </label>
              <input
                className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                id="boost-points"
                min={0}
                onChange={(e) => setBoost(e.target.value)}
                step="0.5"
                type="number"
                value={boost}
              />
            </div>
          </div>
        )}
        <p className="text-muted-foreground text-xs">{strings.cappedNote}</p>
      </section>

      {/* Results */}
      {grades.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label={strings.averageBefore}
              value={fmt(avgBefore, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            />
            <StatCard
              label={strings.averageAfter}
              value={fmt(avgAfter, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            />
            <StatCard
              label={strings.averageChange}
              value={`+${fmt(avgAfter - avgBefore, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`}
            />
            <StatCard
              label={strings.highestBefore}
              value={fmt(maxGrade, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            />
          </div>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-foreground text-lg">
                {strings.beforeAfter}
              </h2>
              <button
                className="rounded-lg border border-border bg-card px-4 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
                onClick={handleCopy}
                type="button"
              >
                {copied ? strings.copied : strings.copyCurvedList}
              </button>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border border-b bg-muted/30">
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.columnNumber}
                    </th>
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.columnOriginal}
                    </th>
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.columnCurved}
                    </th>
                    <th className="px-4 py-2 font-medium text-foreground">
                      {strings.columnChange}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {grades.map((g, i) => (
                    <tr className="border-border/50 border-b" key={`${i}-${g}`}>
                      <td className="px-4 py-2">{i + 1}</td>
                      <td className="px-4 py-2">
                        {fmt(g, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-4 py-2 font-medium text-foreground">
                        {fmt(curved[i], {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                      </td>
                      <td className="px-4 py-2">
                        {`+${fmt(curved[i] - g, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
